import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MapPin, TrendingUp, Clock, Mountain, ArrowLeft, Map as MapIcon, Star, User, Calendar, Image, Download, Flag, Sunrise, Sunset, Camera } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ReviewForm from '@/components/ReviewForm';
import './RouteDetailPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RouteDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  useEffect(() => {
    fetchRouteDetails();
    fetchReviews();
  }, [id]);

  const fetchRouteDetails = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/routes/${id}`, { headers });
      setRoute(response.data);
    } catch (error) {
      console.error('Error fetching route:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/route/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const exportToGPX = () => {
    if (!route) return;

    // Erstelle GPX-Datei
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="PedalScot" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${route.name}</name>
    <desc>${route.description}</desc>
    <author>
      <name>PedalScot</name>
    </author>
  </metadata>
  <trk>
    <name>${route.name}</name>
    <desc>${route.description}</desc>
    <type>${route.activity_type || 'cycling'}</type>
    <trkseg>
      ${route.waypoints && route.waypoints.map(wp => `
      <trkpt lat="${wp.lat}" lon="${wp.lon}">
        <ele>${wp.elevation || 0}</ele>
      </trkpt>`).join('') || ''}
    </trkseg>
  </trk>
</gpx>`;

    // Download-Link erstellen
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${route.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gpx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReviewSubmitted = () => {
    setShowReviewForm(false);
    fetchReviews(); // Refresh reviews
  };

  const handleViewOnMap = () => {
    navigate(`/map?routeId=${id}`);
  };

  const handleReportRoute = async () => {
    if (!reportReason.trim()) {
      alert('Bitte wählen Sie einen Grund aus');
      return;
    }

    try {
      await axios.post(`${API}/report-route`, {
        route_id: id,
        route_name: route.name,
        reason: reportReason,
        details: reportDetails,
        user_email: user?.email || 'anonymous'
      });
      
      alert('Danke für Ihre Meldung! Wir werden das überprüfen.');
      setShowReportModal(false);
      setReportReason('');
      setReportDetails('');
    } catch (error) {
      console.error('Error reporting route:', error);
      alert('Fehler beim Senden der Meldung');
    }
  };

  if (loading) {
    return (
      <div className="route-detail-page">
        <div className="loading">Lade Route...</div>
      </div>
    );
  }

  if (!route) {
    return (
      <div className="route-detail-page">
        <div className="error">Route nicht gefunden</div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="route-detail-page">
      <Link to="/routes" className="back-link">
        <ArrowLeft size={20} /> Zurück zu Routen
      </Link>

      <div className="route-header">
        <div className="route-title-section">
          <h1>{route.name}</h1>
          <div className="route-meta">
            <span className="region">
              <MapPin size={16} /> {route.region}
            </span>
            <span className="rating">
              <Star size={16} fill={averageRating > 0 ? "gold" : "none"} /> 
              {averageRating} ({reviews.length} {t('reviews')})
            </span>
          </div>
        </div>
        <div className="route-actions">
          <button className="view-map-btn" onClick={handleViewOnMap}>
            <MapIcon size={20} /> Auf Karte anzeigen
          </button>
          <button className="export-gpx-btn" onClick={exportToGPX} title="Route als GPX herunterladen">
            <Download size={20} /> GPX Export
          </button>
          <button className="report-route-btn" onClick={() => setShowReportModal(true)} title="Problem melden">
            <Flag size={20} /> Route melden
          </button>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Route melden</h3>
            <p>Bitte teilen Sie uns mit, was nicht stimmt:</p>
            
            <select 
              value={reportReason} 
              onChange={(e) => setReportReason(e.target.value)}
              className="report-select"
            >
              <option value="">-- Grund auswählen --</option>
              <option value="outdated">Veraltete Informationen</option>
              <option value="closed">Weg gesperrt</option>
              <option value="dangerous">Gefährliche Stelle</option>
              <option value="incorrect">Falsche Route</option>
              <option value="other">Sonstiges</option>
            </select>
            
            <textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder="Zusätzliche Details (optional)..."
              className="report-textarea"
              rows="4"
            />
            
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowReportModal(false)}>
                Abbrechen
              </button>
              <button className="btn-submit" onClick={handleReportRoute}>
                Absenden
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="route-content">
        <div className="route-stats">
          <div className="stat-card">
            <TrendingUp size={24} />
            <div>
              <div className="stat-value">{route.distance} km</div>
              <div className="stat-label">Distanz</div>
            </div>
          </div>
          <div className="stat-card">
            <Mountain size={24} />
            <div>
              <div className="stat-value">{route.elevation_gain} m</div>
              <div className="stat-label">Höhenmeter</div>
            </div>
          </div>
          <div className="stat-card">
            <Clock size={24} />
            <div>
              <div className="stat-value">{route.duration}</div>
              <div className="stat-label">Dauer</div>
            </div>
          </div>
          <div className="stat-card">
            <span className={`difficulty-badge ${route.difficulty?.toLowerCase()}`}>
              {route.difficulty}
            </span>
            <div className="stat-label">Schwierigkeit</div>
          </div>
          
          {route.sun_times && (
            <>
              <div className="stat-card sun-times">
                <Sunrise size={24} color="#f59e0b" />
                <div>
                  <div className="stat-value">{route.sun_times.sunrise}</div>
                  <div className="stat-label">Sonnenaufgang</div>
                </div>
              </div>
              <div className="stat-card sun-times">
                <Sunset size={24} color="#f97316" />
                <div>
                  <div className="stat-value">{route.sun_times.sunset}</div>
                  <div className="stat-label">Sonnenuntergang</div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="route-description">
          <h2>Beschreibung</h2>
          <p>{route.description}</p>
        </div>

        {route.highlights && route.highlights.length > 0 && (
          <div className="route-highlights">
            <h2>Highlights</h2>
            <ul>
              {route.highlights.map((highlight, idx) => (
                <li key={idx}>{highlight}</li>
              ))}
            </ul>
          </div>
        )}

        {route.photo_spots && route.photo_spots.length > 0 && (
          <div className="photo-spots-section">
            <h2><Camera size={24} /> Foto-Spots ({route.photo_spots.length})</h2>
            <p className="photo-spots-desc">Perfekte Orte für beeindruckende Aufnahmen</p>
            <div className="photo-spots-grid">
              {route.photo_spots.map((spot, idx) => (
                <div key={idx} className="photo-spot-card">
                  <Camera size={20} className="camera-icon" />
                  <div className="spot-info">
                    <div className="spot-description">{spot.description}</div>
                    <div className="spot-coords">
                      📍 {spot.lat.toFixed(4)}, {spot.lng.toFixed(4)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="route-info-grid">
          <div className="info-item">
            <strong>Fahrradtyp:</strong> {route.bike_type}
          </div>
          <div className="info-item">
            <strong>Routentyp:</strong> {route.route_type}
          </div>
          <div className="info-item">
            <strong>Oberfläche:</strong> {route.surface_type}
          </div>
          <div className="info-item">
            <strong>Verkehr:</strong> {route.traffic}
          </div>
          {route.best_season && (
            <div className="info-item">
              <strong>Beste Zeit:</strong> {route.best_season}
            </div>
          )}
        </div>
      </div>

      <div className="reviews-section">
        <div className="reviews-header">
          <h2>
            <Star size={24} /> Bewertungen ({reviews.length})
          </h2>
          {user ? (
            <button 
              className="add-review-btn" 
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm ? 'Abbrechen' : 'Bewertung schreiben'}
            </button>
          ) : (
            <Link to="/login" className="add-review-btn">
              Login zum Bewerten
            </Link>
          )}
        </div>

        {showReviewForm && (
          <div className="review-form-container">
            <ReviewForm
              itemId={id}
              itemType="route"
              onReviewSubmitted={handleReviewSubmitted}
            />
          </div>
        )}

        <div className="reviews-list">
          {reviews.length === 0 ? (
            <div className="no-reviews">
              <p>Noch keine Bewertungen für diese Route. Sei der Erste!</p>
            </div>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="review-author">
                    <User size={20} />
                    <span>{review.user_name || 'Anonym'}</span>
                  </div>
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        fill={i < review.rating ? 'gold' : 'none'}
                        color="gold"
                      />
                    ))}
                  </div>
                </div>
                <div className="review-date">
                  <Calendar size={14} />
                  {new Date(review.created_at).toLocaleDateString('de-DE')}
                </div>
                <p className="review-text">{review.comment}</p>
                {review.photos && review.photos.length > 0 && (
                  <div className="review-photos">
                    {review.photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Review photo ${idx + 1}`}
                        className="review-photo"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
